import { api } from "@shared/api/client";
import type {
  Task,
  TaskAttachment,
  TaskComment,
  TaskIndexResponse,
  TaskParticipantPermission,
  TaskState,
} from "../types/tasks";

export type CreateTaskPayload = {
  titulo: string;
  descripcion?: string;
  prioridad: "baja" | "media" | "alta";
  asignado_id?: number | null;
  proyecto_id?: number | null;
  fecha_inicio?: string | null;
  fecha_fin?: string | null;
  attachments?: File[];
};

export async function getTasksData(): Promise<TaskIndexResponse> {
  const { data } = await api.get<TaskIndexResponse>("/tasks");
  return data;
}

export async function getTask(id: number): Promise<Task> {
  const { data } = await api.get<{ data: Task }>(`/tasks/${id}`);
  return data.data;
}

export async function createTask(payload: CreateTaskPayload): Promise<Task> {
  const formData = new FormData();
  formData.append("titulo", payload.titulo);
  formData.append("prioridad", payload.prioridad);

  if (payload.descripcion?.trim()) {
    formData.append("descripcion", payload.descripcion.trim());
  }

  if (payload.proyecto_id) {
    formData.append("proyecto_id", String(payload.proyecto_id));
  }

  if (payload.asignado_id !== undefined && payload.asignado_id !== null) {
    formData.append("asignado_id", String(payload.asignado_id));
  }

  if (payload.fecha_inicio) {
    formData.append("fecha_inicio", payload.fecha_inicio);
  }

  if (payload.fecha_fin) {
    formData.append("fecha_fin", payload.fecha_fin);
  }

  payload.attachments?.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data } = await api.post<{ ok: boolean; data: Task }>("/tasks", formData);
  return data.data;
}

export async function changeTaskState(
  taskId: number,
  estado: TaskState,
): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(`/tasks/${taskId}/state`, {
    estado,
  });
  return data.data;
}

export async function reorderTasks(ids: number[]): Promise<void> {
  await api.post("/tasks/reorder", { ids });
}

export async function assignTask(
  taskId: number,
  asignadoId: number,
): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(`/tasks/${taskId}/assign`, {
    asignado_id: asignadoId,
  });
  return data.data;
}

export async function addTaskParticipant(
  taskId: number,
  usuarioId: number,
  permiso: TaskParticipantPermission,
): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(`/tasks/${taskId}/participants`, {
    usuario_id: usuarioId,
    permiso,
  });
  return data.data;
}

export async function removeTaskParticipant(
  taskId: number,
  usuarioId: number,
): Promise<Task> {
  const { data } = await api.delete<{ ok: boolean; data: Task }>(
    `/tasks/${taskId}/participants/${usuarioId}`,
  );
  return data.data;
}

export async function createSubtask(
  taskId: number,
  titulo: string,
): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(`/tasks/${taskId}/subtasks`, {
    titulo,
  });
  return data.data;
}

export async function toggleSubtask(
  taskId: number,
  subtaskId: number,
): Promise<Task> {
  const { data } = await api.patch<{ ok: boolean; data: Task }>(
    `/tasks/${taskId}/subtasks/${subtaskId}/toggle`,
  );
  return data.data;
}

export async function deleteSubtask(
  taskId: number,
  subtaskId: number,
): Promise<Task> {
  const { data } = await api.delete<{ ok: boolean; data: Task }>(
    `/tasks/${taskId}/subtasks/${subtaskId}`,
  );
  return data.data;
}

export async function createTaskComment(
  taskId: number,
  comentario: string,
): Promise<TaskComment> {
  const { data } = await api.post<{ ok: boolean; data: TaskComment }>(`/tasks/${taskId}/comments`, {
    comentario,
  });
  return data.data;
}

export async function uploadTaskAttachments(
  taskId: number,
  files: File[],
): Promise<Task> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data } = await api.post<{ ok: boolean; data: Task }>(
    `/tasks/${taskId}/attachments`,
    formData,
  );
  return data.data;
}

export async function downloadTaskAttachment(
  attachment: TaskAttachment,
): Promise<void> {
  const response = await api.get(attachment.download_url, {
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = attachment.nombre_original;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function deleteTaskAttachment(
  attachmentId: number,
): Promise<Task> {
  const { data } = await api.delete<{ ok: boolean; data: Task }>(
    `/tasks/attachments/${attachmentId}`,
  );
  return data.data;
}

export async function startTaskTime(taskId: number): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(`/tasks/${taskId}/time/start`);
  return data.data;
}

export async function stopTaskTime(
  taskId: number,
  timeEntryId: number,
): Promise<Task> {
  const { data } = await api.post<{ ok: boolean; data: Task }>(
    `/tasks/${taskId}/time/${timeEntryId}/stop`,
  );
  return data.data;
}

export async function deleteTask(taskId: number): Promise<void> {
  await api.delete(`/tasks/${taskId}`);
}
